/**
 * Measured vs estimated vs manual tagging for usage figures.
 *
 * A figure is only "measured" when a provider (or local runtime) stated it
 * exactly. Coarse readings and derived figures stay estimated. Owner-entered
 * allowances are manual. Nothing is upgraded silently.
 */

import type {
  UsageConfidence,
  UsageCostSource,
  UsageReadingOrigin,
} from './contract.js';

export interface MeasurementTag {
  readonly costSource: UsageCostSource;
  /** Short label for meters and Inspector: "measured", "estimated", "manual". */
  readonly label: string;
  /** Longer phrase for tooltips / Overseer copy. */
  readonly phrase: string;
}

/**
 * Map a ledger reading origin + confidence onto the display cost source.
 * `derived` is always estimated — Brunel must not dress an inference as measured.
 */
export function tagMeasurement(
  origin: UsageReadingOrigin,
  confidence: UsageConfidence,
): MeasurementTag {
  if (origin === 'owner_entered') {
    return {
      costSource: 'manual',
      label: 'manual',
      phrase: 'entered by the owner — not reported by a provider',
    };
  }
  if (origin === 'derived') {
    return {
      costSource: 'estimated',
      label: 'estimated',
      phrase: 'estimated — not a provider-stated reading',
    };
  }
  if (
    (origin === 'provider_api' || origin === 'cli_output' || origin === 'local_runtime' || origin === 'account_export')
    && confidence === 'exact'
  ) {
    return {
      costSource: 'measured',
      label: 'measured',
      phrase: 'measured from a provider or runtime reading',
    };
  }
  if (
    (origin === 'provider_api' || origin === 'cli_output' || origin === 'local_runtime' || origin === 'account_export')
    && confidence === 'coarse'
  ) {
    return {
      costSource: 'estimated',
      label: 'estimated',
      phrase: 'coarse provider reading — treated as an estimate',
    };
  }
  return {
    costSource: 'estimated',
    label: 'estimated',
    phrase: 'confidence unknown — shown as an estimate, not a measured figure',
  };
}

/** Compact badge text beside a meter figure. Empty when there is no figure. */
export function measurementBadge(
  hasFigure: boolean,
  origin: UsageReadingOrigin | null,
  confidence: UsageConfidence | null,
): string | null {
  if (!hasFigure) return null;
  return tagMeasurement(origin ?? 'unknown', confidence ?? 'unknown').label;
}

/**
 * Format a numeric usage figure for display without inventing a percentage.
 * Returns null when the value is absent — callers must not substitute 0.
 */
export function formatUsageFigure(
  value: number | null | undefined,
  unit: string,
): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  if (unit === 'percent') return `${trimNumber(value)}%`;
  if (unit === 'tokens') return `${trimNumber(value)} tokens`;
  if (unit === 'credits') return `${trimNumber(value)} credits`;
  if (unit === 'requests') return `${trimNumber(value)} requests`;
  return trimNumber(value);
}

function trimNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  const fixed = value.toFixed(2);
  return fixed.replace(/\.?0+$/, '');
}
